import { useAtomValue } from "jotai";
import React from "react";

import { useUser } from "src/atoms/user";
import { planogramImagesModeAtom } from "src/components/PlanogramExtensions/store/atoms";
import { styledSafe } from "src/utils/styledComponent";
import { urls } from "src/utils/urls";

interface ImageProps extends Props {
  merchantId: number;
  imagesMode: boolean;
  size?: "small" | "original";
}

const ImageElement = styledSafe("div").attrs<ImageProps>(
  ({ merchantId, productCode, size, tileX, tileY }) => ({
    style: {
      backgroundImage: `url(${urls.productImage(productCode, merchantId, size || "original")})`,
      // TILED unit cells: one div with a repeating background replaces a
      // unitsWide × unitsHigh grid of image divs — several-fold fewer DOM
      // nodes and style recalcs on tray/multi-unit pogs.
      ...(tileX && tileY && (tileX > 1 || tileY > 1)
        ? { backgroundSize: `${100 / tileX}% ${100 / tileY}%`, backgroundRepeat: "repeat" }
        : {}),
    },
  }),
)`
  width: ${({ fixedSize }) => fixedSize || "100%"};
  min-width: ${({ fixedSize }) => fixedSize || "100%"};
  height: ${({ fixedSize }) => fixedSize || "100%"};
  min-height: ${({ fixedSize }) => fixedSize || "100%"};
  background-size: ${({ fixedSize }) => (fixedSize ? "contain" : "100% 100%")};
  background-position: center;
  background-repeat: no-repeat;
  visibility: ${({ imagesMode }) => (!imagesMode ? "hidden" : "visible")};
`;

interface Props {
  productCode: string;
  fixedSize?: string;
  size?: "small" | "original";
  /** Repeat the artwork as a tileX × tileY grid inside one element. */
  tileX?: number;
  tileY?: number;
}

export const ProductImage: React.FC<Props> = ({
  productCode,
  fixedSize,
  size = "original",
  tileX,
  tileY,
}) => {
  const { user } = useUser();

  const imagesMode = useAtomValue(planogramImagesModeAtom);

  return (
    <ImageElement
      merchantId={user.merchantId}
      productCode={productCode}
      fixedSize={fixedSize}
      imagesMode={imagesMode}
      tileX={tileX}
      tileY={tileY}
      size={size}
    />
  );
};
